import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, FileCode2, FileText, Receipt } from 'lucide-react';
import { fetchMesFacturesSponsor } from '../services/api';

import { dateFr } from '../utils/dates';
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

const euros = (montant: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    .format(montant);

const statuts: Record<string, { label: string; classe: string }> = {
  payee: { label: 'Payée', classe: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-200' },
  en_attente_paiement: {
    label: 'À régler',
    classe: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200',
  },
  emis: { label: 'Émis', classe: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200' },
  annulee: {
    label: 'Annulée',
    classe: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  },
};

/**
 * Documents comptables du sponsor connecté.
 *
 * La facture part par e-mail au moment du paiement, mais un e-mail se perd et
 * une comptabilité la réclame parfois des mois plus tard.
 *
 * Les fiches tiennent en vignettes, quatre par ligne, repliées : le sponsor
 * cherche presque toujours la dernière, et les autres n'ont pas à occuper
 * l'écran.
 */
export default function MesFacturesSponsor() {
  const { data, isPending } = useQuery({
    queryKey: ['mes-factures-sponsor'],
    queryFn: fetchMesFacturesSponsor,
    retry: false,
  });

  const factures: any[] = data?.factures ?? [];

  if (isPending || factures.length === 0) return null;

  return (
    <section className="space-y-3">
      <h2 className="font-semibold lg:text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2">
        <Receipt className="w-5 h-5 text-primary-600" />
        Mes factures
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {factures.map((f) => (
          <CarteFacture key={f.id} facture={f} />
        ))}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Le PDF porte déjà le XML en pièce embarquée : il suffit à la plupart des logiciels. Le
        fichier XML est fourni à part pour ceux qui ne savent pas l'en extraire.
      </p>
    </section>
  );
}

function CarteFacture({ facture: f }: { facture: any }) {
  const [ouverte, setOuverte] = useState(false);

  const statut = statuts[f.statut] ?? {
    label: f.statut,
    classe: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  };

  return (
    <div className="card p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {f.numero}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {f.type === 'avoir' ? 'Avoir' : 'Facture'} — {euros(f.montant)}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setOuverte(!ouverte)}
          aria-expanded={ouverte}
          aria-label={ouverte ? `Replier la facture ${f.numero}` : `Ouvrir la facture ${f.numero}`}
          className="shrink-0 p-1 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          {ouverte ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${statut.classe}`}>
        {statut.label}
      </span>

      {ouverte && (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
          {f.payeeLe && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Réglée le {dateFr(f.payeeLe)}
            </p>
          )}

          {f.pdfDisponible && (
            <a
              href={`${API_BASE}/sponsor/mes-factures/${f.id}/pdf`}
              className="btn-outline w-full justify-center inline-flex items-center gap-2 text-sm px-3 py-2"
            >
              <FileText className="w-4 h-4" />
              Facture PDF
            </a>
          )}

          {f.xmlDisponible ? (
            <a
              href={`${API_BASE}/sponsor/mes-factures/${f.id}/xml`}
              className="btn-outline w-full justify-center inline-flex items-center gap-2 text-sm px-3 py-2"
            >
              <FileCode2 className="w-4 h-4" />
              XML EN 16931
            </a>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Représentation structurée indisponible sur ce document.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
